import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { str } from '~/components/module/setup/cells';
import { MedicalRecordType, createMedicalRecordAdapter } from '~/apis/medicalRecord';
import { setupRouteAbsolute } from '~/utils/routes/setup';
import { IFormField } from '~/utils/interface/setup';

type Props = {
  type: MedicalRecordType;
  // i18n key under pages.setup (allergies / healthHistory / testResults)
  i18nKey: string;
  permissionAction: string;
};

const MedicalRecordList = ({ type, i18nKey, permissionAction }: Props) => {
  const { t } = useTranslation();
  const adapter = useMemo(() => createMedicalRecordAdapter(type), [type]);
  const isAllergy = type === 'ALLERGY';
  const prefix = `pages.setup.${i18nKey}`;

  const fields: IFormField[] = isAllergy
    ? [
        { name: 'description', labelKey: `${prefix}.substance`, type: 'text', required: true },
        { name: 'note', labelKey: `${prefix}.reaction`, type: 'text' },
      ]
    : [
        { name: 'file', labelKey: 'pages.setup.medicalFile', type: 'file' },
        { name: 'description', labelKey: 'pages.setup.medicalDescription', type: 'text', required: true },
      ];

  const columns = isAllergy
    ? [
        { key: 'description', labelKey: `${prefix}.substance`, render: (form: Record<string, unknown>) => <span className='font-medium text-foreground'>{str(form.description as string)}</span> },
        { key: 'note', labelKey: `${prefix}.reaction`, render: (form: Record<string, unknown>) => str(form.note as string) },
      ]
    : [
        { key: 'description', labelKey: 'pages.setup.medicalDescription', render: (form: Record<string, unknown>) => <span className='font-medium text-foreground'>{str(form.description as string)}</span> },
        {
          key: 'file',
          labelKey: 'pages.setup.medicalFile',
          render: (form: Record<string, unknown>) =>
            typeof form.file === 'string' && form.file ? (
              <a href={form.file} target='_blank' rel='noreferrer' className='underline'>
                {t('pages.setup.currentFile')}
              </a>
            ) : form.file instanceof File ? (
              form.file.name
            ) : null,
        },
      ];

  return (
    <CollectionPage
      i18nPrefix={prefix}
      module='seniorMedical'
      permissionAction={permissionAction}
      backTo={{ path: setupRouteAbsolute.medical, labelKey: 'nav.setup.medical' }}
      adapter={adapter}
      fields={fields}
      defaultForm={{}}
      getTitle={(form) => str(form.description)}
      columns={columns}
    />
  );
};

export default MedicalRecordList;
